import { Add, Delete, Edit, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { IPaginationData } from '../../../api/base';
import categoryApi from '../../../api/category.api';
import ColorPicker from '../../../components/ColorPicker';
import Table, { Order } from '../../../components/Table';
import { EComponent } from '../../../models/base';
import { ICategory } from '../../../models/category.model';
import theme from '../../../theme';

export default function CategoryListPage() {
  const [component, setComponent] = useState<EComponent | 'all'>('all');

  const [dataSource, setDataSource] = useState<IPaginationData<ICategory>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });
  const [loading, setLoading] = useState(false);
  const [[orderBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >(['order', 'asc']);

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [currItem, setCurrItem] = useState<Partial<ICategory> | undefined>();
  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    categoryApi
      .listing({
        ...filter,
        component: component !== 'all' ? component : undefined,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: orderBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filter, component, order, orderBy]);

  useEffect(loadData, [loadData]);

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">Start-up</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <div>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => {
                setCurrItem({ order: 0 });
              }}
            >
              Create new
            </Button>
          </div>
        </div>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          variant="fullWidth"
          value={component}
          onChange={(ev, v) => setComponent(v)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 700,
              fontSize: 18,
            },
          }}
        >
          <Tab label="All" value="all" />
          {Object.values(EComponent).map((t) => (
            <Tab key={t} label={_.capitalize(_.words(t).join(' '))} value={t} />
          ))}
        </Tabs>
      </Box>
      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={orderBy}
        loading={loading}
        // onClick={setCurrItem}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'order',
            title: 'Order',
            dataKey: 'order',
            sortable: true,
          },
          {
            key: 'title',
            title: 'Title',
            dataKey: 'title_en',
            sortable: true,
            render: (data) => (
              <>
                <div className="one-lines">{data.title_vi}</div>
                <div className="one-lines">{data.title_en}</div>
              </>
            ),
          },

          {
            key: 'component',
            title: 'Component',
            dataKey: 'component',
            render: (data) => _.capitalize(_.words(data.component).join(' ')),
          },
          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            sortable: true,
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            sortable: true,
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'actions',
            title: 'Actions',
            dataKey: 'id',
            render: (data) => (
              <>
                <IconButton onClick={() => setCurrItem(data)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />

      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this submission?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                categoryApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete submission successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!currItem} onClose={() => setCurrDeletedId(undefined)}>
        <DialogTitle>
          {currItem?.id ? 'Update start-up' : 'Create new start-up'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" p={1}>
                Order
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Order"
                size="small"
                fullWidth
                type="number"
                value={currItem?.order || 0}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      order: +(e.currentTarget.value || 0),
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" p={1}>
                Title
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="English"
                size="small"
                fullWidth
                type="text"
                value={currItem?.title_en}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      title_en: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Vietnamese"
                size="small"
                type="text"
                fullWidth
                value={currItem?.title_vi}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      title_vi: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Component</InputLabel>
                <Select
                  label="Component"
                  placeholder="Select component"
                  value={currItem?.component || ''}
                  onChange={(e) => {
                    if (currItem) {
                      setCurrItem({
                        ...currItem,
                        component: e.target.value
                          ? (e.target.value as EComponent)
                          : undefined,
                      });
                    }
                  }}
                >
                  {Object.values(EComponent).map((t) => (
                    <MenuItem key={t} value={t}>
                      {_.capitalize(_.words(t).join(' '))}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <div style={{ marginBottom: 8, lineHeight: '40px' }}>Color:</div>
              <ColorPicker
                value={currItem?.color}
                onChange={(value) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      color: value || null,
                    });
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrItem(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            variant="contained"
            onClick={() => {
              if (!currItem) {
                return;
              }
              Promise.resolve()
                .then(() => {
                  setDeleting(true);
                })
                .then(() => {
                  if (currItem?.id) {
                    return categoryApi
                      .update(currItem.id, currItem)
                      .then(() => {
                        toast.success('Update successful');
                      })
                      .catch((e) => {
                        toast.error(e.message ?? e.toString());
                        throw e;
                      });
                  }
                  return categoryApi
                    .create(currItem)
                    .then(() => {
                      toast.success('Update successful');
                    })
                    .catch((e) => {
                      toast.error(e.message ?? e.toString());
                      throw e;
                    });
                })
                .then(() => {
                  loadData();
                })
                .finally(() => {
                  setCurrItem(undefined);
                  setDeleting(false);
                });
            }}
            color="primary"
          >
            {deleting ? (
              <CircularProgress size={16} />
            ) : currItem?.id ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
